import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useHomecareRooms } from "@/hooks/useHomecareRooms";
import RoomChatButton from "@/components/RoomChatButton";
import { formatDateID, formatDateLong, formatRp, waLink, renderTemplate, DEFAULT_TEMPLATES } from "@/lib/constants";
import { StatusBadge, ServiceBadge, PaymentBadge } from "@/components/Badges";
import RescheduleDialog from "@/components/RescheduleDialog";
import RefundDialog from "@/components/RefundDialog";
import PaymentReviewDialog from "@/components/PaymentReviewDialog";
import PaymentAdjustDialog from "@/components/PaymentAdjustDialog";
import MidtransPayBlock from "@/components/MidtransPayBlock";
import ImunisasiFill from "@/components/ImunisasiFill";
import BillItemsPanel from "@/components/BillItemsPanel";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ObatMoney } from "@/components/ObatMoney";
import Receipt from "@/components/Receipt";
import {
  WhatsappLogo, CheckCircle, XCircle, PencilSimple, ArrowsClockwise, ArrowUUpLeft,
  ShieldCheck, FlagCheckered, MapPin, X, UserSwitch, House, Pill, Receipt as ReceiptIcon,
} from "@phosphor-icons/react";

// A single label/value row — renders nothing when value is empty.
function Field({ label, value }) {
  if (value === null || value === undefined || value === "" || (Array.isArray(value) && value.length === 0)) return null;
  return (
    <div className="flex justify-between gap-4 border-b border-rose-50 py-2 text-sm last:border-0">
      <span className="shrink-0 text-slate-500">{label}</span>
      <span className="text-right font-semibold text-slate-800">{value}</span>
    </div>
  );
}

function Section({ title, icon, children }) {
  return (
    <div className="rounded-2xl border border-rose-100/70 bg-white p-4">
      <p className="mb-1 flex items-center gap-1.5 font-heading text-sm font-extrabold text-[#DB2777]">{icon}{title}</p>
      <div>{children}</div>
    </div>
  );
}

const isHomecare = (r) => /homecare/i.test(`${r.lokasi || ""} ${r.jenis_layanan || ""} ${r.kategori_layanan || ""}`) || Number(r.biaya_homecare || 0) > 0;

const servesArea = (staff, kel) => {
  if (!staff || (staff.homecare_area_mode || "all") !== "restricted") return true;
  const k = String(kel || "");
  return (staff.homecare_areas || []).some((a) => { const c = String((a && a.kode) || a || ""); return c && (k === c || k.startsWith(c + ".")); });
};

export default function ReservationDetail({ reservation, open, onClose, onChanged }) {
  const r = reservation;
  const navigate = useNavigate();
  const { user } = useAuth();
  const { byReservation } = useHomecareRooms(0);
  const isAdmin = user?.role === "admin" || user?.role === "super_admin";
  const isSuper = user?.role === "super_admin";

  const [settings, setSettings] = useState(null);
  const [payData, setPayData] = useState(null);
  const [obatCharges, setObatCharges] = useState(null);
  const [staffList, setStaffList] = useState([]);
  const [reschedule, setReschedule] = useState(null);
  const [refundRes, setRefundRes] = useState(null);
  const [review, setReview] = useState(null);
  const [adjust, setAdjust] = useState(null);
  const [busy, setBusy] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendConfirm, setResendConfirm] = useState(false);
  const [opDialog, setOpDialog] = useState(null); // {type:'selesai'|'noshow'|'cancelreq'}
  const [opNote, setOpNote] = useState("");
  const [receiptOpen, setReceiptOpen] = useState(false);
  const canReceipt = isAdmin || user?.can_manage_billing === true;

  useEffect(() => {
    if (open) api.get("/public/settings").then((res) => setSettings(res.data)).catch(() => setSettings({}));
    if (open && isAdmin) api.get("/staff").then((res) => setStaffList(res.data)).catch(() => {});
  }, [open, isAdmin]);

  useEffect(() => {
    setPayData(null);
    setObatCharges(null);
    if (open && r?.id && r?.payment_mode && r.payment_mode !== "none") {
      api.get(`/payments/reservation/${r.id}`).then((res) => setPayData(res.data)).catch(() => setPayData(null));
    }
    if (open && r?.id && (isAdmin || user?.can_manage_billing || user?.can_pharmacy)) {
      api.get(`/pharmacy/charges/reservation/${r.id}`)
        .then((res) => setObatCharges(res.data))
        .catch(() => setObatCharges(null));
    }
  }, [open, r?.id, r?.payment_mode, isAdmin, user?.can_manage_billing, user?.can_pharmacy]);

  const reloadObat = async () => {
    try { const res = await api.get(`/pharmacy/charges/reservation/${r.id}`); setObatCharges(res.data); } catch (e) { toast.error(apiError(e)); }
  };

  const changed = useCallback(() => { onChanged && onChanged(); }, [onChanged]);

  const doAttendance = async (status, catatan) => {
    setBusy(true);
    try { await api.patch(`/reservations/${r.id}/attendance`, { status, catatan }); toast.success(`Status diperbarui: ${status}`); changed(); onClose(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const doCancelRequest = async (alasan) => {
    setBusy(true);
    try { await api.post(`/reservations/${r.id}/cancel-request`, { alasan }); toast.success("Pengajuan batal terkirim ke Admin"); changed(); onClose(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const resolveCancel = async (action) => {
    setBusy(true);
    try { await api.post(`/reservations/${r.id}/cancel-request/${action}`); toast.success(action === "approve" ? "Pengajuan disetujui — reservasi dibatalkan" : "Pengajuan batal ditolak"); changed(); onClose(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const viewProof = async (fid) => {
    try {
      const res = await api.get(`/payments/proof/${fid}`, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch (e) { /* ignore */ }
  };

  const tplMsg = (key) =>
    renderTemplate((settings && settings[key]) || DEFAULT_TEMPLATES[key], {
      nama: r?.nama_lengkap, layanan: r?.jenis_layanan, kategori: r?.kategori_layanan || "",
      tanggal: r ? formatDateLong(r.tanggal_kunjungan) : "", jam: r?.jam_kunjungan,
      lokasi: r?.lokasi || "", petugas: r?.petugas_nama || "",
    });

  const setStatus = async (status, waKey) => {
    setBusy(true);
    try {
      await api.patch(`/reservations/${r.id}/status`, { status });
      toast.success(`Status diubah menjadi ${status}`);
      if (waKey) setTimeout(() => window.open(waLink(r.no_whatsapp, tplMsg(waKey)), "_blank"), 250);
      changed();
      onClose();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const assign = async (petugas_nama) => {
    try { await api.patch(`/reservations/${r.id}/assign`, { petugas_nama }); toast.success("Petugas ditugaskan"); changed(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const resendLink = async () => {
    setResending(true);
    try {
      const { data } = await api.post(`/reservations/${r.id}/resend-payment-link`);
      const st = data.wa_status;
      if (st === "sent") toast.success("Link pembayaran terkirim via WhatsApp");
      else if (st === "stubbed") toast.success("Link tercatat (mode uji — WA belum aktif)");
      else if (st === "invalid_phone") toast.error("Nomor WhatsApp pasien tidak valid");
      else toast.error("Gagal mengirim WhatsApp. Silakan coba lagi.");
    } catch (e) { toast.error(apiError(e)); }
    finally { setResending(false); setResendConfirm(false); }
  };

  if (!r) return null;
  const ad = r.alamat_data || {};
  const homecare = isHomecare(r);
  const paid = r.payment_mode && r.payment_mode !== "none";
  const canConfirm = isAdmin && ["Baru", "Menunggu Konfirmasi", "Reschedule"].includes(r.status);
  const canReject = isAdmin && !["Batal", "Selesai", "No Show"].includes(r.status);
  const canFinish = isAdmin && ["Dikonfirmasi", "Sudah Datang"].includes(r.status);
  const canVerifyPay = isAdmin && r.payment_status === "Menunggu Verifikasi";
  const canRefund = isSuper && paid && r.status !== "Batal";
  const isPetugas = user?.role === "petugas";
  const mine = isPetugas && r.petugas_nama && r.petugas_nama === user?.petugas_nama;
  const notTerminal = !["Selesai", "No Show", "Batal", "Dibatalkan Otomatis"].includes(r.status);
  const pendingCancel = r.cancel_request && r.cancel_request.status === "pending";
  const mapsUrl = r.maps_link || (ad.maps_link) || null;
  const openRme = async () => {
    try { const { data } = await api.get(`/rme/encounter-by-reservation/${r.id}`); navigate(`/rekam-medis/${data.encounter_id}`); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
        <DialogContent
          data-testid="reservation-detail"
          className="flex max-h-[92vh] flex-col gap-0 overflow-hidden p-0 sm:max-w-lg max-sm:h-[100dvh] max-sm:max-h-[100dvh] max-sm:max-w-full max-sm:rounded-none"
        >
          {/* Sticky header */}
          <div className="sticky top-0 z-10 border-b border-rose-100 bg-white/95 px-5 py-4 backdrop-blur">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <DialogTitle asChild><h2 className="truncate font-heading text-xl font-extrabold text-slate-800">{r.nama_lengkap}</h2></DialogTitle>
                <DialogDescription className="sr-only">Detail reservasi {r.nama_lengkap} — {r.jenis_layanan}</DialogDescription>
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  <ServiceBadge service={r.jenis_layanan} />
                  <StatusBadge status={r.status} />
                  <PaymentBadge status={r.payment_status} />
                </div>
                {r.auto_confirm_note && (
                  <p data-testid="auto-confirm-note" className={`mt-1.5 rounded-lg px-2 py-1 text-xs font-semibold ${r.auto_confirmed ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
                    {r.auto_confirm_note}{r.auto_confirm_wa_failed ? " · WA konfirmasi gagal terkirim" : ""}
                  </p>
                )}
                {r.status === "Sudah Datang" && (
                  <button data-testid="open-rme-btn" onClick={openRme} className="mt-2 flex items-center gap-1.5 rounded-lg bg-[#7C4DE0] px-3 py-1.5 text-xs font-bold text-white">Buka Rekam Medis</button>
                )}
              </div>
              <button data-testid="detail-close" onClick={onClose} className="shrink-0 text-slate-400 hover:text-slate-600"><X size={22} weight="bold" /></button>
            </div>
          </div>

          {/* Scrollable body */}
          <div className="flex-1 space-y-3 overflow-y-auto px-5 py-4">
            {/* A. Data Pasien */}
            <Section title="Data Pasien">
              <Field label="Nama" value={r.nama_lengkap} />
              <Field label="NIK" value={r.nik} />
              <Field label="Tanggal Lahir" value={r.tanggal_lahir ? formatDateID(r.tanggal_lahir) : null} />
              <Field label="Usia" value={r.usia_text || (r.usia ? `${r.usia} tahun` : null)} />
              <Field label="Jenis Kelamin" value={r.jenis_kelamin} />
              {isAdmin && <Field label="No. WhatsApp" value={r.no_whatsapp} />}
            </Section>

            {/* B. Data Kunjungan */}
            <Section title="Data Kunjungan">
              <Field label="Layanan" value={r.jenis_layanan} />
              <Field label="Kategori" value={r.kategori_layanan} />
              <Field label="Tanggal" value={formatDateLong(r.tanggal_kunjungan)} />
              <Field label="Jam" value={r.jam_kunjungan} />
              <Field label="Lokasi" value={r.lokasi} />
              <Field label="Petugas" value={r.petugas_nama} />
              <Field label="Status Reservasi" value={<StatusBadge status={r.status} />} />
            </Section>

            {/* C. Data Klinis / Tambahan (conditional) */}
            {(r.hpht || r.usia_kehamilan_text || r.usia_kehamilan_minggu || r.hpl || r.kehamilan_ke ||
              r.nama_bayi || r.tanggal_lahir_bayi || r.nama_ibu || r.nik_ibu ||
              (Array.isArray(r.jenis_vaksin) ? r.jenis_vaksin.length : r.jenis_vaksin) ||
              r.imunisasi_terakhir || r.tanggal_imunisasi_terakhir || r.riwayat_kesehatan ||
              r.keluhan || r.catatan_admin || r.consent_accepted) && (
              <Section title="Data Klinis / Tambahan">
                <Field label="HPHT" value={r.hpht ? formatDateID(r.hpht) : null} />
                <Field label="Usia Kehamilan" value={r.usia_kehamilan_text || (r.usia_kehamilan_minggu ? `${r.usia_kehamilan_minggu} minggu` : null)} />
                <Field label="HPL" value={r.hpl ? formatDateID(r.hpl) : null} />
                <Field label="Kehamilan ke-" value={r.kehamilan_ke} />
                <Field label="Jumlah Persalinan" value={r.jumlah_persalinan} />
                <Field label="Jumlah Keguguran" value={r.jumlah_keguguran} />
                <Field label="Nama Bayi / Anak" value={r.nama_bayi} />
                <Field label="Tgl Lahir Bayi / Anak" value={r.tanggal_lahir_bayi ? formatDateID(r.tanggal_lahir_bayi) : null} />
                <Field label="Jenis Kelamin Bayi" value={r.jenis_kelamin_bayi} />
                <Field label="Nama Ibu / Orang Tua" value={r.nama_ibu} />
                <Field label="NIK Ibu" value={r.nik_ibu} />
                <Field label="Jenis Vaksin" value={Array.isArray(r.jenis_vaksin) ? r.jenis_vaksin.join(", ") : r.jenis_vaksin} />
                <Field label="Vaksin Terakhir" value={r.imunisasi_terakhir} />
                <Field label="Tgl Vaksin Terakhir" value={r.tanggal_imunisasi_terakhir ? formatDateID(r.tanggal_imunisasi_terakhir) : null} />
                <Field label="Riwayat Kesehatan" value={r.riwayat_kesehatan} />
                <Field label="Keluhan" value={r.keluhan} />
                <Field label="Catatan Admin" value={r.catatan_admin} />
                <Field label="Persetujuan Imunisasi" value={r.consent_accepted ? "✓ Disetujui" : null} />
              </Section>
            )}

            <ImunisasiFill reservation={r} onChanged={onChanged} />

            {/* D. Alamat / Homecare (conditional) */}
            {(r.alamat || ad.provinsi_nama || homecare) && (
              <Section title={homecare ? "Alamat Homecare" : "Alamat"} icon={homecare ? <House size={16} weight="duotone" /> : <MapPin size={16} weight="duotone" />}>
                <Field label="Jalan / Perumahan" value={ad.jalan} />
                <Field label="No. Rumah" value={ad.no_rumah} />
                <Field label="RT / RW" value={ad.rt || ad.rw ? `${ad.rt || "-"} / ${ad.rw || "-"}` : null} />
                <Field label="Kelurahan / Desa" value={ad.kelurahan_nama} />
                <Field label="Kecamatan" value={ad.kecamatan_nama} />
                <Field label="Kota / Kabupaten" value={ad.kabupaten_nama} />
                <Field label="Provinsi" value={ad.provinsi_nama} />
                <Field label="Kode Pos" value={ad.kode_pos} />
                {r.alamat && !ad.provinsi_nama && <Field label="Alamat" value={r.alamat} />}
                <Field label="Patokan / Catatan" value={r.patokan} />
                {mapsUrl && (
                  <a data-testid="detail-maps" href={mapsUrl} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1.5 rounded-lg bg-sky-50 px-3 py-2 text-xs font-bold text-sky-700 hover:bg-sky-100">
                    <MapPin size={14} weight="fill" /> Buka di Google Maps
                  </a>
                )}
              </Section>
            )}

            {/* E. Pembayaran (conditional) */}
            {(r.pricing_mode === "per_item" || paid || (payData && payData.payments && payData.payments.length > 0) || (isAdmin && Number(r.total_tagihan || 0) > 0) || (obatCharges && Number(obatCharges.total_obat || 0) > 0)) && (
              <Section title="Pembayaran">
                {r.pricing_mode === "per_item" && (
                  <BillItemsPanel
                    reservation={r}
                    canEdit={isAdmin || !!user?.can_manage_billing}
                    canManualPrice={isAdmin}
                    isSuperAdmin={isSuper}
                    onChanged={onChanged}
                  />
                )}
                {Number(r.diskon_nominal || 0) > 0 && <Field label="Harga Awal" value={formatRp(r.harga_awal || r.total_tagihan)} />}
                {Number(r.diskon_nominal || 0) > 0 && <Field label="Diskon" value={`− ${formatRp(r.diskon_nominal)}${r.diskon_type === "persen" ? ` (${r.diskon_value}%)` : ""}`} />}
                <Field label="Total Tagihan" value={formatRp(r.total_tagihan)} />
                <Field label="Mode Pembayaran" value={r.payment_mode === "dp" ? "DP (Uang Muka)" : r.payment_mode === "full" ? "Bayar Penuh" : r.payment_mode} />
                <Field label="Total Dibayar" value={formatRp(r.total_dibayar)} />
                <Field label="Sisa Tagihan" value={formatRp(r.sisa_tagihan)} />
                <Field label="Status Pembayaran" value={<PaymentBadge status={r.payment_status} />} />
                {payData && payData.payments && payData.payments.length > 0 && (
                  <div className="mt-2">
                    <p className="mb-1 text-xs font-semibold text-slate-500">Riwayat Pembayaran</p>
                    {payData.payments.map((p) => (
                      <div key={p.id} className="flex items-center justify-between gap-2 border-b border-rose-50 py-1.5 text-xs last:border-0">
                        <span className="text-slate-600">{formatRp(p.amount)} · {p.method} · {p.kind}</span>
                        <span className="flex items-center gap-1.5">
                          <span className="font-semibold text-slate-700">{p.status}</span>
                          {p.proof_file_id && <button data-testid={`detail-proof-${p.id}`} onClick={() => viewProof(p.proof_file_id)} className="rounded bg-slate-200 px-1.5 py-0.5 text-[10px] font-bold text-slate-600">Bukti</button>}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
                {obatCharges && Number(obatCharges.total_obat || 0) > 0 && (
                  <div className="mt-3 rounded-xl border border-[#FBCFE8] bg-[#FDF2F8] p-3" data-testid="detail-obat-charges">
                    <p className="mb-1.5 flex items-center gap-1.5 text-xs font-bold text-[#DB2777]"><Pill size={14} weight="duotone" /> Tagihan Obat / Farmasi</p>
                    {(obatCharges.charges || []).filter((c) => c.status !== "Void" || (c.money && c.money.bersih > 0)).map((c) => (
                      <div key={c.id} data-testid={`detail-obat-charge-${c.id}`}>
                      {(c.items || []).map((it, i) => {
                        const net = it.subtotal - (it.harga_satuan * (it.returned_qty || 0));
                        return (
                          <div key={`${c.id}-${i}`} className="flex items-center justify-between border-b border-rose-100 py-1 text-xs">
                            <span className="text-slate-600">{it.nama} ×{it.jumlah}{(it.returned_qty || 0) > 0 ? ` · retur ${it.returned_qty}` : ""}</span>
                            <span className="font-semibold text-slate-700">{formatRp(net)}</span>
                          </div>
                        );
                      })}
                      <ObatMoney c={c} user={user} onDone={reloadObat} />
                      </div>
                    ))}
                    <div className="mt-1 flex items-center justify-between text-xs font-bold">
                      <span className="text-slate-600">Subtotal Obat</span>
                      <span className="text-[#DB2777]" data-testid="detail-obat-net">{formatRp(obatCharges.net_obat)}</span>
                    </div>
                    <div className="mt-1 flex items-center justify-between border-t border-rose-200 pt-1.5 text-sm font-extrabold">
                      <span className="text-slate-700">Total Layanan + Obat</span>
                      <span className="text-[#6A38C7]" data-testid="detail-grand-total">{formatRp(Number(r.total_tagihan || 0) + Number(obatCharges.net_obat || 0))}</span>
                    </div>
                  </div>
                )}
                {r.policy_accepted && (
                  <p className="mt-2 rounded-lg bg-emerald-50 px-2 py-1.5 text-[11px] text-emerald-700">✓ Pasien menyetujui ketentuan (v{r.policy_version || "1"})</p>
                )}
                {isAdmin && r.status !== "Batal" && (
                  <button data-testid="detail-edit-payment" onClick={() => setAdjust(r)}
                    className="mt-3 inline-flex items-center gap-1.5 rounded-xl bg-[#EEE6FF] px-3 py-2 text-xs font-bold text-[#6A38C7] hover:bg-[#e0d2ff] active:scale-95">
                    <PencilSimple size={14} weight="bold" /> Edit Pembayaran / Diskon
                  </button>
                )}
                {isAdmin && r.status !== "Batal" && r.payment_mode !== "none"
                  && Number(r.sisa_tagihan ?? ((r.total_tagihan || 0) - (r.total_dibayar || 0))) > 0 && (
                  <MidtransPayBlock kind="reservation" id={r.id} sisa={r.sisa_tagihan ?? ((r.total_tagihan || 0) - (r.total_dibayar || 0))} isAdmin={isAdmin}
                    defaultAmount={r.payment_mode === "dp"
                      ? (Math.max(0, Math.round((r.dp_required || 0) - (r.total_dibayar || 0))) || Math.round(r.sisa_tagihan ?? ((r.total_tagihan || 0) - (r.total_dibayar || 0))))
                      : Math.round(r.sisa_tagihan ?? ((r.total_tagihan || 0) - (r.total_dibayar || 0)))}
                    onPaid={() => { changed(); onClose(); }} />
                )}
                {isAdmin && paid && r.status !== "Batal"
                  && Number(r.sisa_tagihan ?? ((r.total_tagihan || 0) - (r.total_dibayar || 0))) > 0 && (
                  <div className="mt-3">
                    {!resendConfirm ? (
                      <button data-testid="detail-resend-link" onClick={() => setResendConfirm(true)}
                        className="inline-flex items-center gap-1.5 rounded-xl bg-[#25D366]/10 px-3 py-2 text-xs font-bold text-[#0f9d58] hover:bg-[#25D366]/20 active:scale-95">
                        <WhatsappLogo size={14} weight="fill" /> Kirim Ulang Link Pembayaran
                      </button>
                    ) : (
                      <div className="flex flex-wrap items-center gap-2 rounded-xl bg-emerald-50 px-3 py-2">
                        <span className="text-xs font-semibold text-emerald-800">Kirim link pembayaran ke WhatsApp pasien?</span>
                        <button data-testid="detail-resend-confirm" disabled={resending} onClick={resendLink}
                          className="rounded-lg bg-emerald-600 px-2.5 py-1 text-xs font-bold text-white disabled:opacity-50">
                          {resending ? "Mengirim…" : "Ya, Kirim"}
                        </button>
                        <button data-testid="detail-resend-cancel" onClick={() => setResendConfirm(false)}
                          className="rounded-lg bg-white px-2.5 py-1 text-xs font-bold text-slate-500 hover:bg-slate-50">Batal</button>
                      </div>
                    )}
                  </div>
                )}
              </Section>
            )}

            {/* Refund info */}
            {r.status === "Batal" && (r.refund_amount > 0 || r.refund_type) && (
              <Section title="Pembatalan & Refund" icon={<ArrowUUpLeft size={16} weight="duotone" />}>
                <Field label="Tipe" value={r.refund_type === "full" ? "Refund Penuh" : r.refund_type === "partial" ? "Refund Sebagian" : "Tidak Refund"} />
                <Field label="Pembayaran Awal" value={formatRp(r.original_paid)} />
                <Field label="Nominal Refund" value={formatRp(r.refund_amount)} />
                <Field label="Alasan" value={r.refund_reason} />
              </Section>
            )}

            {/* Assign petugas (admin, non-final) */}
            {isAdmin && !["Batal", "Selesai", "No Show"].includes(r.status) && (
              <Section title="Tugaskan Petugas" icon={<UserSwitch size={16} weight="duotone" />}>
                <select data-testid="detail-assign" defaultValue={r.petugas_nama || ""} onChange={(e) => assign(e.target.value)}
                  className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-[#7C4DE0]">
                  <option value="">-- Pilih Petugas --</option>
                  {staffList.map((s) => <option key={s.id} value={s.nama}>{s.nama} ({s.jenis})</option>)}
                </select>
                {homecare && (r.kelurahan || ad.kelurahan) && r.petugas_nama && !servesArea(staffList.find((s) => s.nama === r.petugas_nama), r.kelurahan || ad.kelurahan) && (
                  <p data-testid="detail-area-warning" className="mt-2 rounded-lg bg-amber-50 px-3 py-2 text-[11px] font-semibold text-amber-700">⚠️ Petugas ini berada di luar wilayah Homecare yang ditetapkan.</p>
                )}
              </Section>
            )}
          </div>

          {/* Sticky action footer */}
          {isAdmin && pendingCancel && (
            <div data-testid="admin-cancel-request" className="mx-5 mb-2 rounded-xl border border-amber-300 bg-amber-50 p-3 text-sm">
              <p className="font-bold text-amber-800">Pengajuan Batal oleh Petugas</p>
              <p className="text-amber-700">Alasan: {r.cancel_request.alasan}</p>
              <p className="text-xs text-amber-600">Oleh {r.cancel_request.by_name} · {String(r.cancel_request.requested_at || "").slice(0, 16).replace("T", " ")}</p>
              <div className="mt-2 flex gap-2">
                <button data-testid="cancel-approve" disabled={busy} onClick={() => resolveCancel("approve")} className="rounded-lg bg-rose-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-rose-700">Setujui &amp; Batalkan</button>
                <button data-testid="cancel-reject" disabled={busy} onClick={() => resolveCancel("reject")} className="rounded-lg bg-slate-200 px-3 py-1.5 text-xs font-bold text-slate-700 hover:bg-slate-300">Tolak</button>
              </div>
            </div>
          )}
          <div className="sticky bottom-0 z-10 flex flex-wrap gap-2 border-t border-rose-100 bg-white/95 px-5 py-3 backdrop-blur">
            <RoomChatButton reservation={r} roomInfo={byReservation[String(r.id)]} size="sm" />
            {isAdmin && (
              <a data-testid="detail-wa" href={waLink(r.no_whatsapp, tplMsg("tpl_approved"))} target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-1.5 rounded-xl bg-[#25D366] px-3 py-2 text-xs font-bold text-white active:scale-95">
                <WhatsappLogo size={16} weight="fill" /> WA Pasien
              </a>
            )}
            {canReceipt && <ActBtn testid="detail-receipt" onClick={() => setReceiptOpen(true)} className="bg-[#EEE6FF] text-[#6A38C7] hover:bg-[#e0d2ff]"><ReceiptIcon size={16} weight="duotone" /> Kwitansi</ActBtn>}
            {canConfirm && <ActBtn testid="detail-confirm" disabled={busy} onClick={() => setStatus("Dikonfirmasi", "tpl_approved")} className="bg-[#7C4DE0] text-white hover:bg-[#6A38C7]"><CheckCircle size={16} weight="bold" /> Terima &amp; Konfirmasi</ActBtn>}
            {canVerifyPay && <ActBtn testid="detail-verify-pay" onClick={() => setReview(r)} className="bg-amber-500 text-white hover:bg-amber-600"><ShieldCheck size={16} weight="bold" /> Konfirmasi Pembayaran</ActBtn>}
            {isAdmin && <ActBtn testid="detail-edit" onClick={() => { onClose(); navigate(`/reservasi/${r.id}/edit`); }} className="bg-slate-100 text-slate-700 hover:bg-slate-200"><PencilSimple size={16} /> Edit</ActBtn>}
            {isAdmin && <ActBtn testid="detail-reschedule" onClick={() => setReschedule(r)} className="bg-orange-100 text-orange-700 hover:bg-orange-200"><ArrowsClockwise size={16} /> Reschedule</ActBtn>}
            {canFinish && <ActBtn testid="detail-finish" disabled={busy} onClick={() => setStatus("Selesai")} className="bg-emerald-600 text-white hover:bg-emerald-700"><FlagCheckered size={16} weight="bold" /> Tandai Selesai</ActBtn>}
            {canRefund && <ActBtn testid="detail-refund" onClick={() => setRefundRes(r)} className="bg-rose-100 text-rose-600 hover:bg-rose-200"><ArrowUUpLeft size={16} /> Batalkan &amp; Refund</ActBtn>}
            {canReject && !canRefund && <ActBtn testid="detail-reject" disabled={busy} onClick={() => setStatus("Batal", "tpl_rejected")} className="bg-rose-50 text-rose-600 hover:bg-rose-100"><XCircle size={16} /> Tolak</ActBtn>}
            {mine && notTerminal && r.status !== "Sudah Datang" && <ActBtn testid="petugas-serving" disabled={busy} onClick={() => doAttendance("Sudah Datang")} className="bg-sky-100 text-sky-700 hover:bg-sky-200"><House size={16} weight="bold" /> Sedang Dilayani</ActBtn>}
            {mine && ["Dikonfirmasi", "Sudah Datang"].includes(r.status) && <ActBtn testid="petugas-finish" disabled={busy} onClick={() => { setOpNote(""); setOpDialog({ type: "selesai" }); }} className="bg-emerald-600 text-white hover:bg-emerald-700"><FlagCheckered size={16} weight="bold" /> Tandai Selesai</ActBtn>}
            {mine && notTerminal && <ActBtn testid="petugas-noshow" disabled={busy} onClick={() => { setOpNote(""); setOpDialog({ type: "noshow" }); }} className="bg-neutral-200 text-neutral-700 hover:bg-neutral-300"><XCircle size={16} weight="bold" /> Tidak Hadir</ActBtn>}
            {mine && notTerminal && !pendingCancel && <ActBtn testid="petugas-cancelreq" disabled={busy} onClick={() => { setOpNote(""); setOpDialog({ type: "cancelreq" }); }} className="bg-rose-50 text-rose-600 hover:bg-rose-100"><ArrowUUpLeft size={16} weight="bold" /> Ajukan Batal (pasien)</ActBtn>}
            {mine && pendingCancel && <span data-testid="petugas-cancel-pending" className="inline-flex items-center gap-1 rounded-xl bg-amber-100 px-3 py-2 text-xs font-bold text-amber-700">Pengajuan batal menunggu Admin</span>}
          </div>
        </DialogContent>
      </Dialog>

      {canReceipt && <Receipt tx={r} open={receiptOpen} onClose={() => setReceiptOpen(false)} />}
      <RescheduleDialog reservation={reschedule} open={!!reschedule} onClose={() => setReschedule(null)} onDone={() => { setReschedule(null); changed(); onClose(); }} />

      <Dialog open={!!opDialog} onOpenChange={(v) => !v && setOpDialog(null)}>
        <DialogContent className="rounded-2xl sm:max-w-sm" data-testid="petugas-op-dialog" aria-describedby={undefined}>
          <DialogTitle className="font-heading">{opDialog?.type === "selesai" ? "Tandai Selesai?" : opDialog?.type === "noshow" ? "Tandai Tidak Hadir?" : "Ajukan Batal (oleh pasien)?"}</DialogTitle>
          <p className="text-sm text-slate-500">{opDialog?.type === "selesai" ? "Status akan menjadi Selesai dan tidak dapat Anda buka kembali." : opDialog?.type === "noshow" ? "Pasien dianggap tidak hadir. Wajib isi catatan." : "Pengajuan dikirim ke Admin; status & jadwal tidak berubah sampai Admin menyetujui. Wajib isi alasan."}</p>
          {opDialog && opDialog.type !== "selesai" && <textarea data-testid="op-note" value={opNote} onChange={(e) => setOpNote(e.target.value)} rows={3} placeholder="Catatan / alasan" className="mt-2 w-full rounded-xl border border-slate-200 p-2 text-sm outline-none focus:border-[#7C4DE0]" />}
          <div className="mt-4 flex justify-end gap-2">
            <button onClick={() => setOpDialog(null)} className="rounded-xl bg-slate-100 px-4 py-2 text-sm font-bold text-slate-600">Batal</button>
            <button data-testid="op-confirm" disabled={busy || (opDialog && opDialog.type !== "selesai" && !opNote.trim())} onClick={() => { const t = opDialog.type; setOpDialog(null); if (t === "selesai") doAttendance("Selesai"); else if (t === "noshow") doAttendance("No Show", opNote.trim()); else doCancelRequest(opNote.trim()); }} className="rounded-xl bg-[#7C4DE0] px-4 py-2 text-sm font-bold text-white disabled:opacity-50">Konfirmasi</button>
          </div>
        </DialogContent>
      </Dialog>
      <RefundDialog reservation={refundRes} open={!!refundRes} onClose={() => setRefundRes(null)} onDone={() => { setRefundRes(null); changed(); onClose(); }} />
      <PaymentReviewDialog reservation={review} open={!!review} onClose={() => setReview(null)} onChange={() => { changed(); }} />
      <PaymentAdjustDialog reservation={adjust} open={!!adjust} onClose={() => setAdjust(null)} onDone={() => { setAdjust(null); changed(); onClose(); }} />
    </>
  );
}

function ActBtn({ children, onClick, className, testid, disabled }) {
  return (
    <button data-testid={testid} onClick={onClick} disabled={disabled}
      className={`inline-flex items-center gap-1.5 rounded-xl px-3 py-2 text-xs font-bold transition-colors active:scale-95 disabled:opacity-50 ${className}`}>
      {children}
    </button>
  );
}
